import type { FormDialogRef } from '@/components'
import {
  Button,
  FAIcon,
  Field,
  FieldGroup,
  FieldLabel,
  FormDialog,
  NumberInput,
  Textarea,
} from '@/components'
import { cn } from '@/lib/utils'
import { useFormWatch } from '@/utils'
import { useMutation } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useEffect, useMemo, useRef, type ReactNode } from 'react'
import { isMobile } from 'react-device-detect'
import { Controller, useForm } from 'react-hook-form'
import z from 'zod'
import {
  CREATE_SALARY_CORRECTION_MUTATION,
  PATCH_SALARY_CORRECTION_MUTATION,
} from '../mutations'
import { DepartmentCombobox } from '@/components/combobox/department-combox'
import VehicleSelect from '@/components/vehicle-select'
import type { VehicleSelectNode } from '@/modules/vehicles'
import { FacilityCombobox } from '@/components/combobox/facility-combobox'
import { SalaryCodeCombobox } from '@/components/combobox/salary-code-combobox'
import type { SalaryCorrectionNode } from '../types.graphql'

function useSalaryCorrectionSchema() {
  const translations = useTranslate({
    userRequired: 'validation.field-required',
  })

  return z.object({
    id: z.string().optional(),
    user: z.string().min(1, translations.userRequired),
    vehicle: z.string(),
    department: z.string(),
    facility: z.string(),
    salaryCode: z.string(),
    reason: z.string(),
    amount: z.number(),
  })
}

export type SalaryCorrectionForm = z.infer<
  ReturnType<typeof useSalaryCorrectionSchema>
>

type SalaryCorrection = SalaryCorrectionForm & { id: string }
type SalaryCorrectionPrefill = Partial<
  Pick<
    SalaryCorrectionForm,
    'department' | 'facility' | 'salaryCode' | 'reason' | 'amount'
  >
>

export interface SalaryCorrectionDialogRef {
  open(salaryCorrection: SalaryCorrection): void
}

interface Props {
  correction?: SalaryCorrectionNode
  userId: string
  year: number
  month: number

  hideTrigger?: boolean
  prefill?: SalaryCorrectionPrefill
  trigger?: ReactNode

  onSubmit?(): void
}

export function SalaryCorrectionDialog({
  correction,
  userId,
  year,
  month,
  hideTrigger = false,
  prefill,
  trigger,
  onSubmit,
}: Props) {
  const translations = useTranslate({
    labels: {
      user: 'common.user',
      coment: 'common.comment',
      vehicle: 'common.vehicle',
      department: 'common.department',
      facility: 'common.facility',
      value: 'common.value',
      salaryCode: 'common.salary-code',
    },
    headerAdd: 'salary.salary-correction-dialog.header-add',
    headerEdit: 'salary.salary-correction-dialog.header-edit',
    add: 'common.add',
  })

  const dialogRef = useRef<FormDialogRef>(null)
  const correctionDepartment = correction?.department?.id
  const correctionFacility = correction?.facility?.id
  const correctionSalaryCode = correction?.salaryCode?.id
  const correctionReason = correction?.reason
  const correctionAmount = correction?.amount
  const correctionId = correction?.id
  const prefillDepartment = prefill?.department
  const prefillFacility = prefill?.facility
  const prefillSalaryCode = prefill?.salaryCode
  const prefillReason = prefill?.reason
  const prefillAmount = prefill?.amount

  /**
   * `amount` is typed as a number but the Decimal scalar serializes to a
   * string, which fails the zod schema and leaves the confirm button disabled
   * for the whole edit dialog.
   */
  const initialFilters = useMemo(
    () => ({
      user: userId,
      vehicle: '',
      department: correctionDepartment ?? prefillDepartment ?? '',
      facility: correctionFacility ?? prefillFacility ?? '',
      salaryCode: correctionSalaryCode ?? prefillSalaryCode ?? '',
      reason: correctionReason ?? prefillReason ?? '',
      amount: Number(correctionAmount ?? prefillAmount ?? 0),
      id: correctionId ?? '',
    }),
    [
      userId,
      correctionAmount,
      correctionDepartment,
      correctionFacility,
      correctionId,
      correctionReason,
      correctionSalaryCode,
      prefillAmount,
      prefillDepartment,
      prefillFacility,
      prefillReason,
      prefillSalaryCode,
    ]
  )

  const form = useForm<SalaryCorrectionForm>({
    resolver: zodResolver(useSalaryCorrectionSchema()),
    defaultValues: initialFilters,
  })

  useEffect(() => {
    form.reset(initialFilters)
  }, [form, initialFilters])

  const salaryCode = useFormWatch(form, 'salaryCode')

  function handleVehicleChange(vehicle: VehicleSelectNode | null) {
    form.setValue('vehicle', vehicle?.id ?? '')

    if (!vehicle) return

    if (vehicle.department?.id) {
      form.setValue('department', vehicle.department.id)
    }

    if (vehicle.facility?.id) {
      form.setValue('facility', vehicle.facility.id)
    }
  }

  const salaryCorrectionId = useFormWatch(form, 'id')

  const [createSalaryCorrection] = useMutation(
    CREATE_SALARY_CORRECTION_MUTATION,
    {
      refetchQueries: [],
      onCompleted() {
        dialogRef.current?.close()
      },
    }
  )

  const [patchSalaryCorrection] = useMutation(
    PATCH_SALARY_CORRECTION_MUTATION,
    {
      refetchQueries: [],
      onCompleted() {
        dialogRef.current?.close()
      },
    }
  )
  const confirmDisabled = !salaryCode

  function resetForm() {
    setTimeout(() => {
      form.reset(initialFilters)
    }, 300)
  }

  async function handleSubmit(values: SalaryCorrectionForm) {
    if (!values.id)
      await createSalaryCorrection({
        variables: {
          input: {
            user: userId,
            reason: values.reason,
            department: values.department,
            facility: values.facility,
            salaryCode: values.salaryCode,
            amount: values.amount,
            year: year,
            month: month,
          },
        },
      })
    else
      await patchSalaryCorrection({
        variables: {
          id: values.id,
          input: {
            user: userId,
            reason: values.reason,
            department: values.department,
            facility: values.facility,
            salaryCode: values.salaryCode,
            amount: values.amount,
            year: year,
            month: month,
          },
        },
      })
    onSubmit?.()
  }

  return (
    <FormDialog
      ref={dialogRef}
      form={form}
      trigger={
        trigger ?? (
          <Button className={cn(hideTrigger && 'hidden')} size="sm">
            <FAIcon icon={correction ? 'edit' : 'plus'} />
            {!correction ? <span>{translations.add}</span> : null}
          </Button>
        )
      }
      header={
        !salaryCorrectionId ? translations.headerAdd : translations.headerEdit
      }
      noCloseOnEscape
      confirmDisabled={confirmDisabled}
      error={undefined}
      onClose={resetForm}
      onSubmit={handleSubmit}
    >
      <FieldGroup>
        <Controller
          name="amount"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={!!fieldState.error}>
              <FieldLabel>{translations.labels.value}</FieldLabel>
              <NumberInput {...field} decimalScale={2} />
            </Field>
          )}
        />
        <Controller
          name="salaryCode"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={!!fieldState.error}>
              <FieldLabel>{translations.labels.salaryCode}</FieldLabel>
              <SalaryCodeCombobox
                {...field}
                mode="single"
                onChange={value => {
                  field.onChange(value)
                }}
              />
            </Field>
          )}
        />
        <Controller
          name="vehicle"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={!!fieldState.error}>
              <FieldLabel>{translations.labels.vehicle}</FieldLabel>
              <VehicleSelect
                {...field}
                mode="single"
                clearable
                onChange={handleVehicleChange}
              />
            </Field>
          )}
        />
        <Controller
          name="department"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={!!fieldState.error}>
              <FieldLabel>{translations.labels.department}</FieldLabel>
              <DepartmentCombobox
                {...field}
                mode="single"
                clearable
                onChange={value => {
                  field.onChange(value)
                }}
              />
            </Field>
          )}
        />
        <Controller
          name="facility"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={!!fieldState.error}>
              <FieldLabel>{translations.labels.facility}</FieldLabel>
              <FacilityCombobox
                {...field}
                mode="single"
                clearable
                onChange={value => {
                  field.onChange(value)
                }}
              />
            </Field>
          )}
        />
        <Controller
          name="reason"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={!!fieldState.error}>
              <FieldLabel>{translations.labels.coment}</FieldLabel>

              <Textarea {...field} rows={isMobile ? 6 : 4} />
            </Field>
          )}
        />
      </FieldGroup>
    </FormDialog>
  )
}
